import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ViewIds } from '@ardoq/api-types';
import { t } from '@ardoq/i18n';
import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import {
  RadialChart,
  type PolarViewSettings,
  type RadialChartProps,
} from '@ardoq/xyz-charts';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { ChartEmptyState } from 'tabview/chartShared/ChartEmptyState';
import { useRadialChartHost } from '../useRadialChartHost';
import type { PolarSourceData } from './sourceData/buildPolarSourceData';
import type { PolarViewProperties } from './types';

const PolarLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ChartArea = styled(VisualizationContainer)`
  overflow: hidden;
  min-height: 0;
`;

export type PolarViewProps = PolarSourceData & {
  viewSettings: PolarViewSettings;
};

export const PolarView = ({
  plot,
  points,
  viewSettings,
  angleTitle,
  radiusTitle,
  isViewEmpty,
  emptyReason,
  excludedCount,
  numericFields,
  seriesFields,
  viewInstanceId,
  locale,
  i18nLocale,
}: PolarViewProps) => {
  const {
    setContainer,
    container,
    chartColors,
    createExportCanvas,
    onExportCanvasAvailable,
    format,
  } = useRadialChartHost(locale);

  useEffect(() => {
    registerViewPane({
      viewId: ViewIds.POLAR,
      viewProperties: {
        viewSettings,
        numericFields,
        seriesFields,
        i18nLocale,
        isEmptyView: isViewEmpty,
        viewInstanceId,
      } satisfies PolarViewProperties,
      isLegendActive: false,
      getContainer: () => container,
      createExportCanvas,
      viewInstanceId,
    });
  }, [
    viewSettings,
    numericFields,
    seriesFields,
    i18nLocale,
    isViewEmpty,
    viewInstanceId,
    container,
    createExportCanvas,
  ]);

  const formatOtherSeries = useCallback(
    (count: number) =>
      t(
        i18nLocale,
        'views:radialCharts.otherSeries',
        { count },
        '{{count}} more'
      ),
    [i18nLocale]
  );

  const formatAccessibleName = useMemo<
    RadialChartProps['formatAccessibleName']
  >(
    () =>
      ({ pointCount }) =>
        t(
          i18nLocale,
          pointCount === 1
            ? 'views:polar.accessibleName_one'
            : 'views:polar.accessibleName_other',
          { count: pointCount, angle: angleTitle, radius: radiusTitle },
          pointCount === 1
            ? 'Polar chart, 1 component. Angle: {{angle}}. Radius: {{radius}}. Use the arrow keys to read the values.'
            : 'Polar chart, {{count}} components. Angle: {{angle}}. Radius: {{radius}}. Use the arrow keys to read the values.'
        ),
    [i18nLocale, angleTitle, radiusTitle]
  );

  const angleCaption = t(
    i18nLocale,
    'views:polar.caption.angle',
    { field: angleTitle },
    'Angle: {{field}}'
  );
  const radiusCaption = t(
    i18nLocale,
    'views:polar.caption.radius',
    { field: radiusTitle },
    'Radius: {{field}}'
  );
  const axisCaptions = useMemo(
    () => [angleCaption, radiusCaption],
    [angleCaption, radiusCaption]
  );

  const exclusionNotice = t(
    i18nLocale,
    'views:polar.exclusionNotice',
    { plotted: points.length, total: points.length + excludedCount },
    '{{plotted}} of {{total}} components plotted. Components missing a value for either chosen field are not shown.'
  );
  const hasExclusions = excludedCount > 0 && !isViewEmpty;
  const exportCaptions = useMemo(
    () => (hasExclusions ? [exclusionNotice] : []),
    [hasExclusions, exclusionNotice]
  );

  const header = (
    <FlexBox
      padding={hasExclusions ? 'small' : 'none'}
      paddingBottom="none"
      role="status"
      aria-live="polite"
    >
      {hasExclusions && (
        <Text color="textSubtle" variant="caption">
          {exclusionNotice}
        </Text>
      )}
    </FlexBox>
  );

  if (isViewEmpty) {
    return (
      <PolarLayout ref={setContainer}>
        {header}
        <ChartEmptyState
          emptyReason={emptyReason}
          noFieldsSelected={{
            title: t(
              i18nLocale,
              'views:polar.emptyState.noFieldsSelected.title',
              'Choose the fields to plot'
            ),
            message: t(
              i18nLocale,
              'views:polar.emptyState.noFieldsSelected.message',
              'This view reads two numeric fields: one for the angle around the circle and one for the distance from its center. Pick a field for each in the settings bar above.'
            ),
          }}
          noComponents={{
            title: t(
              i18nLocale,
              'views:polar.emptyState.noComponents.title',
              'There is nothing in view to plot'
            ),
            message: t(
              i18nLocale,
              'views:polar.emptyState.noComponents.message',
              'No components are in view, so there are no points to plot.'
            ),
          }}
          tooFewPositions={{
            title: t(
              i18nLocale,
              'views:polar.emptyState.noValues.title',
              'No component has values for both fields'
            ),
            explanation: t(
              i18nLocale,
              'views:polar.emptyState.noValues.message1',
              'A component is plotted only when it has a value for both the angle field and the radius field.'
            ),
            remedy: t(
              i18nLocale,
              'views:polar.emptyState.noValues.message2',
              'Choose fields that the components in this viewpoint have values for.'
            ),
          }}
        />
      </PolarLayout>
    );
  }

  return (
    <PolarLayout ref={setContainer}>
      {header}
      <ChartArea>
        <RadialChart
          plot={plot}
          markType={viewSettings.markType}
          format={format}
          formatOtherSeries={formatOtherSeries}
          formatAccessibleName={formatAccessibleName}
          axisCaptions={axisCaptions}
          onExportCanvasAvailable={onExportCanvasAvailable}
          exportCaptions={exportCaptions}
          {...chartColors}
        />
      </ChartArea>
    </PolarLayout>
  );
};
